// Features extension point (EW-140): which optional product features one customer gets.
//
// ADR-0026 puts unfinished or not-yet-activated work behind a feature flag so `main` stays
// releasable. This is that mechanism's carrier: the flags are declared in the ROOT of a
// distribution config, `pack` bakes the resolved map into the deploy bundle and
// `build-desktop` bakes it into the installer. There is deliberately NO runtime flag service
// and no admin-editable switch — a flag an admin could flip at runtime would void the ADR's
// guarantee that no customer receives a feature before it is finished.
//
// The catalog below is the product's list of gateable features. Each entry states where the
// flag has to bite, so both sides of one feature are switched off by ONE declaration:
//   - `clientExtensions` — built-in extension ids of the desktop client the feature owns.
//     The packaged client is handed these ids (not the feature keys) with their resolved state,
//     so the fork needs no vocabulary of ours.
//   - `serverRoutes` — control-plane route prefixes, as the request URL sees them (global
//     `/api` prefix included, and `/mcp/*` outside it). The gate answers 404 there.
//
// `defaultEnabled` is what a customer distribution gets when its config stays silent about the
// feature. Every feature defaults to `false`, the control-plane-only knowledge base included: a
// customer receives one only where the config names it with `true` — opt-in, never opt-out, so a
// distribution that never heard of a feature cannot ship it. One rule, no exceptions: the product
// ships optional features switched off and the customer config says which are on
// (docs/platform-kialakitasa/6-extension-pointok-es-distribution.md, since 2026-06-22; the EW-140
// Technical details name the built-in extensions this applies to). Our own base and dev builds
// are untouched by these defaults — they carry no gate at all, and an unset ENABLED_FEATURES
// means every feature on (see parseEnabledFeatures).
//
// STRICT ON PURPOSE, like every extension-point schema: an unknown or mistyped feature key
// fails `validate` loudly instead of being dropped as a no-op.
//
// ONLY WHAT CAN ACTUALLY BE ENFORCED belongs in the catalog. A key that switches nothing off is
// worse than an absent one: a customer declares `false`, the config validates, the bundle states
// the feature as disabled — and the installed product still offers it. That is why the built-in
// image generation, Google Workspace and Ollama extensions are NOT here yet: their only surfaces
// are the built-in extension cards, which this fork's settings page does not render (its
// `quickConnect` is empty by product decision), and their server halves live in the client's own
// loopback server, which no distribution flag reaches today. Adding them means giving them an
// enforcement point first.
import { z } from 'zod';
export const PRODUCT_FEATURES = [
    {
        key: 'desktop-browser',
        title: 'OpenWork Browser',
        description: 'The built-in browser panel the agent can drive from a session.',
        defaultEnabled: false,
        clientExtensions: ['openwork-browser'],
        serverRoutes: [],
    },
    {
        key: 'computer-use',
        title: 'Computer Use',
        description: 'macOS-only control of desktop applications through the accessibility runtime.',
        defaultEnabled: false,
        clientExtensions: ['computer-use'],
        serverRoutes: [],
    },
    {
        key: 'voice-mode',
        title: 'Voice Mode',
        description: 'Spoken conversation through the OpenAI realtime API.',
        defaultEnabled: false,
        clientExtensions: ['openwork-voice'],
        serverRoutes: [],
    },
    {
        key: 'knowledge-base',
        title: 'Knowledge bases',
        description: 'Retrieval knowledge bases: the admin surface that builds them and the per-KB MCP connector the clients search through.',
        defaultEnabled: false,
        clientExtensions: [],
        // The admin CRUD + document upload, the per-document download on the client surface, and
        // the MCP host that serves the connector (outside /api — see app.factory).
        serverRoutes: ['/api/admin/knowledge-bases', '/api/me/kb-document', '/mcp/kb'],
    },
];
/** Every declared feature key. */
export const FEATURE_KEYS = PRODUCT_FEATURES.map((feature) => feature.key);
export const productFeature = (key) => PRODUCT_FEATURES.find((feature) => feature.key === key);
/**
 * The declared switches: every key optional, so a config states only what it changes.
 *
 * Built from the catalog rather than written out, because two hand-kept lists of the same keys
 * drift — and the drifted half would be the one that silently accepts a key nothing reads. The
 * published JSON Schema is generated from this, so an IDE offers the same keys.
 */
export const FeaturesSchema = z.strictObject(Object.fromEntries(PRODUCT_FEATURES.map((feature) => [
    feature.key,
    z
        .boolean()
        .optional()
        .describe(`${feature.title} — ${feature.description} (default: ${feature.defaultEnabled})`),
])));
/** Every feature with its effective state: the declaration where there is one, else the default. */
export function resolveFeatures(declared) {
    const values = declared ?? {};
    return Object.fromEntries(PRODUCT_FEATURES.map((feature) => [feature.key, values[feature.key] ?? feature.defaultEnabled]));
}
/**
 * Feature states from the raw env value every deployed consumer receives.
 *
 * Absent and empty mean different things, and the difference is load-bearing: no value at all
 * (unset, or the JSON null a runtime config carries) means every feature on, while an empty
 * string means every gateable feature is off. Unset is our own dev, test and demo environments,
 * which have no distribution and show the whole product; the catalog defaults are all off and
 * would take the knowledge base away from them. A generated deployment always writes the whole
 * answer, so unset never occurs in a customer install and an empty value is deliberate.
 *
 * An unknown key is reported and otherwise ignored: it cannot enable anything, and failing over
 * it would take a deployment down for a stale name left in a config after a feature was retired.
 * Silence is the one thing it must not get — a typo would look exactly like a feature
 * deliberately switched off.
 */
export function parseEnabledFeatures(raw, onUnknown) {
    if (raw === undefined || raw === null)
        return Object.fromEntries(FEATURE_KEYS.map((key) => [key, true]));
    const declared = new Set(raw
        .split(',')
        .map((key) => key.trim())
        .filter((key) => key.length > 0));
    const unknown = [...declared].filter((key) => !FEATURE_KEYS.includes(key));
    if (unknown.length > 0)
        onUnknown?.(unknown);
    return Object.fromEntries(PRODUCT_FEATURES.map((feature) => [feature.key, declared.has(feature.key)]));
}
/** The enabled feature keys, sorted — the deterministic form that travels in a bundle. */
export function enabledFeatureKeys(declared) {
    return Object.entries(resolveFeatures(declared))
        .filter(([, enabled]) => enabled)
        .map(([key]) => key)
        .sort();
}
/**
 * Every gateable desktop extension id with its effective state, keys sorted.
 *
 * ONE map, not a list of what to switch off. The same value has to decide both halves of the
 * client-side gate: a `false` id is not offered at all, and a `true` id is offered AND starts
 * switched on where the user has not chosen otherwise. A disabled-only list cannot say the
 * second half — a feature the customer explicitly asked for would arrive present but switched
 * off, because the product's own manifest defaults that toggle to off (`computer-use`,
 * `openwork-voice`). One declaration, one resolved value, both halves.
 *
 * Extension IDS, not feature keys: the fork owns the ids already, so a feature the product
 * renames or regroups stays a product-side change. Sorted, like everything else that travels
 * into an artifact, so the same config bakes in identical bytes.
 */
export function clientExtensionEnablement(declared) {
    const resolved = resolveFeatures(declared);
    const entries = PRODUCT_FEATURES.flatMap((feature) => feature.clientExtensions.map((id) => [id, resolved[feature.key] === true]));
    return Object.fromEntries(entries.sort(([left], [right]) => left.localeCompare(right)));
}
/**
 * The feature that owns a request path, or undefined where no feature does.
 *
 * Prefix match on path SEGMENTS: a bare `startsWith` would also match a sibling route whose
 * name merely begins with a gated one (`/api/admin/knowledge-bases-export`), and gating a
 * route nobody asked to gate is as much a defect as leaking a gated one.
 */
export function featureOwningRoute(pathname) {
    const path = pathname.split('?')[0] ?? pathname;
    return PRODUCT_FEATURES.find((feature) => feature.serverRoutes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`)));
}
//# sourceMappingURL=features.js.map